/**
 * Messages, banners, watermarks, progress and overlays (Design System §11–12, §16). No colour for
 * severity and no icons: a rule, a bold sentence saying what happened, a second saying what to do.
 */
import type { MouseEvent, ReactNode as RN } from "react";
import { WATERMARK } from "@oc/copy";
import { h, cx, type ReactNode } from "./h.ts";
import { Button } from "./controls.ts";

export type MessageKind = "hard" | "normal" | "quiet";

export function Message({ kind = "normal", what, todo, actions }: { kind?: MessageKind; what: string; todo?: string; actions?: ReactNode }): ReactNode {
  return h("div", { className: cx("msg", kind === "hard" && "hard", kind === "quiet" && "quiet"), role: kind === "hard" ? "alert" : "status" },
    kind === "quiet" ? h("span", null, what) : h("b", null, what),
    todo ? h("span", null, todo) : null,
    actions ? h("div", { className: "actions" }, actions) : null,
  );
}

/** A rule and a sentence, never a coloured box. Text comes from packages/copy (holdNotice) or the API. */
export function Banner({ kind, text }: { kind: "held" | "shadow"; text: string }): ReactNode {
  return h("div", { className: kind === "held" ? "oc-banner-held" : "oc-banner-shadow", role: "status" }, text);
}

/** Outlined type, never filled. DRAFT while the reviewer field is empty; INTERNAL on the deal screen. */
export function Watermark({ kind }: { kind: "draft" | "internal" }): ReactNode {
  return h("span", { className: "oc-watermark", "aria-hidden": "true" }, kind === "draft" ? WATERMARK.draft : "INTERNAL");
}

/** A 3px rule that fills. No spinners — a long job says how long. */
export function Progress({ fraction, label }: { fraction: number; label: string }): ReactNode {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  return h("div", { role: "progressbar", "aria-valuenow": Math.round(pct), "aria-valuemin": 0, "aria-valuemax": 100, "aria-label": label },
    h("div", { className: "oc-caption" }, label),
    h("div", { className: "prog" }, h("i", { style: { width: `${pct}%` } })));
}

/** The ruled ledger line of ticks — twelve on the intake, six on the walkthrough. Never a percentage. */
export function Ticks({ count, current, label }: { count: number; current: number; label: string }): ReactNode {
  return h("div", { className: "oc-ticks", role: "progressbar", "aria-label": label, "aria-valuenow": current + 1, "aria-valuemin": 1, "aria-valuemax": count },
    ...Array.from({ length: count }, (_, i) => h("span", { key: i, className: cx(i < current && "done", i === current && "now") })));
}

export interface DialogAction { label: string; onClick: () => void | Promise<void | boolean>; primary?: boolean; quiet?: boolean; disabled?: boolean; consequence?: string }

/** Ink top rule, no shadow, scrim is paper at 80%. Controlled: the caller owns `open`. */
export function Dialog({ open, title, onClose, children, actions }: { open: boolean; title: string; onClose: () => void; children: RN; actions: DialogAction[] }): ReactNode {
  if (!open) return null;
  return h("div", { className: "dialog-scrim", onClick: (e: MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) onClose(); } },
    h("div", { className: "dialog", role: "dialog", "aria-modal": "true", "aria-label": title },
      h("div", { className: "hd" }, h("div", { className: "title" }, title), children),
      h("div", { className: "ft" }, ...actions.map((a) => h(Button, {
        key: a.label, label: a.label, consequence: a.consequence, kind: a.primary ? "primary" : a.quiet ? "quiet" : "secondary",
        disabled: a.disabled,
        onClick: async () => { const r = await a.onClick(); if (r !== false) onClose(); },
      }))),
    ));
}

/** Plus and minus, not a chevron; no animation on open. */
export function Disclosure({ summary, open, onToggle, children }: { summary: string; open: boolean; onToggle: () => void; children: RN }): ReactNode {
  return h("div", { className: "oc-disclosure" },
    h("button", { type: "button", "aria-expanded": open ? "true" : "false", onClick: onToggle }, h("span", null, summary), h("span", { "aria-hidden": "true" }, open ? "−" : "+")),
    open ? h("div", { className: "panel" }, children) : null,
  );
}

/** The annotation component: ochre, small, always a phrase; never a link or a button inside it. */
export function MarginNote({ lead, lines }: { lead?: string; lines: string[] }): ReactNode {
  return h("div", { className: "oc-margin-note" }, lead ? h("b", null, lead) : null, ...lines.map((l, i) => h("div", { key: i }, l)));
}

/** "j / k move row · ↵ open · 1 2 3 confidence …" — every console action has a key. */
export function KeyboardHint({ keys }: { keys: Array<[string, string]> }): ReactNode {
  return h("div", { className: "kbd" }, ...keys.flatMap(([k, what], i) => [i > 0 ? " · " : null, h("b", { key: k }, k), ` ${what}`]));
}
